import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';

export enum Role {
  ADMIN = 'ADMIN',
  OPS = 'OPS',
  TECH = 'TECH',
  PARTNER = 'PARTNER',
  DOWNSTREAM = 'DOWNSTREAM',
}

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export function useAuth() {
  const navigate = useNavigate();
  const { token, user, setAuth, logout } = useAuthStore();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      logout();
      navigate('/login');
    }, INACTIVITY_TIMEOUT);
  }, [logout, navigate]);

  useEffect(() => {
    if (token) {
      const events = ['mousemove', 'keydown', 'click', 'scroll'];
      events.forEach((e) => window.addEventListener(e, resetTimer));
      resetTimer();
      return () => {
        events.forEach((e) => window.removeEventListener(e, resetTimer));
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }
  }, [token, resetTimer]);

  const login = useCallback(
    async (username: string, password: string) => {
      const resp = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || '登录失败');
      }

      const data = await resp.json();
      setAuth(data.token, data.user);
      return data.user;
    },
    [setAuth],
  );

  return { token, user, isLoggedIn: !!token, login, logout };
}
